import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@this/shared/ui/feedbacks/modal';
import { Alert } from '@this/shared/ui/feedbacks/alert';
import { Button } from '@this/shared/ui/inputs/button';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { fetchUserChatRule, updateUserChatRule } from '../api/userChatRule';

type Props = {
  open: boolean;
  onClose: () => void;
};

const UserChatRuleSettingsModal = ({ open, onClose }: Props): JSX.Element => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [calendarRuleText, setCalendarRuleText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    let mounted = true;
    setIsLoading(true);
    setError(null);
    setNotice(null);

    fetchUserChatRule()
      .then(rule => {
        if (!mounted) return;
        setCalendarRuleText(rule.calendarRuleText);
      })
      .catch(() => {
        if (!mounted) return;
        setError('設定の読み込みに失敗しました。');
      })
      .finally(() => {
        if (!mounted) return;
        setIsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [open]);

  const handleRuleTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCalendarRuleText(e.target.value);
    setNotice(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    setError(null);
    setNotice(null);

    try {
      const rule = await updateUserChatRule({ calendarRuleText });
      setCalendarRuleText(rule.calendarRuleText);
      setNotice('保存しました。');
    } catch {
      setError('保存に失敗しました。');
    } finally {
      setIsSubmitting(false);
    }
  }, [calendarRuleText]);

  return (
    <Modal size="small" open={open} onClose={onClose}>
      <ModalHeader>個人ルール</ModalHeader>
      <ModalBody>
        <div style={styles.formContent}>
          {error && <Alert variant="danger">{error}</Alert>}
          {notice && <Alert variant="success">{notice}</Alert>}
          <label style={styles.formGroup}>
            <span style={styles.label}>カレンダー連携時のルール</span>
            <textarea
              value={calendarRuleText}
              disabled={isLoading || isSubmitting}
              onChange={handleRuleTextChange}
              placeholder="例：土日は候補として提案しない。月曜午前の移動は避ける。"
              style={styles.textarea}
            />
          </label>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="sub" onClick={onClose} disabled={isSubmitting}>
          キャンセル
        </Button>
        <Button color="primary" onClick={handleSubmit} disabled={isLoading || isSubmitting}>
          {isSubmitting ? '保存中...' : '保存'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const getStyles = (theme: AITTheme) => ({
  formContent: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px'
  },
  label: {
    fontSize: '14px',
    fontWeight: 500,
    color: theme.textColor || '#333333'
  },
  textarea: {
    width: '100%',
    minHeight: '160px',
    resize: 'vertical' as const,
    padding: '10px 12px',
    fontSize: '14px',
    lineHeight: 1.6,
    border: `1px solid ${theme.grayBorderColor || '#e5e7eb'}`,
    borderRadius: '6px',
    outline: 'none',
    boxSizing: 'border-box' as const
  }
});

export default UserChatRuleSettingsModal;
